import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ScheduledChannelSnapshotFrequency } from 'dashboard/enums/scheduled-channel-snapshot-frequency.enum';
import { ScheduledChannelSnapshotRunStatusType } from 'dashboard/enums/scheduled-channel-snapshot-run-status-type.enum';
import { ScheduledChannelSnapshotListingsSelectionMode } from 'dashboard/enums/scheduled-channel-snapshot-selection-mode.enum';
import { ScheduledChannelSnapshot } from 'dashboard/interfaces/scheduled-channel-snapshot.interface';
import { map, Observable } from 'rxjs';
import { dayArray } from 'shared/constants/days.constants';
import { Days, IndividualDay } from 'shared/interfaces/days-of-week.interface';

const SUPPLIER_MANAGEMENT_API_URL = environment.BASE_URL + 'supplierManagementApi/';

@Injectable()
export class ScheduledChannelSnapshotsService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private http: HttpClient,
    ) {
        this.apollo = this.apolloProvider.use('suppliersAPI');
    }

    private mapDayBitmaskToObject(days: number): Days {
        const bitmask = days as unknown as number;
        const daysResult: Days = {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        };

        // Iterate through each day and set the corresponding day in daysResult
        dayArray.forEach((day: IndividualDay, index: number) => {
            // This shifts the 1 (0000001) to the left by 6 - index
            // The result is then compared to the bitmask using a bitwise AND
            // ex. 1 (0000001) << 5 = 0100000; 0100000 & 1011111 = 0000000 (false)
            daysResult[day] = Boolean(bitmask & (1 << (6 - index)));
        });

        return daysResult;
    }

    private mapDayObjectToBitmask(dayObject: Days | undefined): number | undefined {
        if (!dayObject) {
            return undefined;
        }

        let bitmask = 0;

        // Iterate through each day and set the corresponding bit in bitmask
        dayArray.forEach((day: IndividualDay, index: number) => {
            if (dayObject[day]) {
                // This shifts the 1 (0000001) to the left by 6 - index
                // The result is then compared to the bitmask using a bitwise OR
                // ex. 1 (0000001) << 5 = 0100000; 0100000 | 1011111 = 1111111
                bitmask |= 1 << (6 - index);
            }
        });

        return bitmask == 0 ? undefined : bitmask;
    }

    private deleteEmptyAndPrepareDataForGraphQL(data: any) {
        this.deleteEmptyProperties(data);
        return this.stringifyIntoGraphQLQuery(data);
    }

    private stringifyIntoGraphQLQuery(data: any) {
        return JSON.stringify(data).replace(/"([^"]+)":/g, '$1:');
    }

    private deleteEmptyProperties(data: any) {
        Object.keys(data).forEach((key) => {
            if ((data as any)[key] === undefined) {
                delete (data as any)[key];
            }
        });
    }

    private addSecondsIfNeeded(time: string): string {
        return time.split(':').length === 2 ? `${time}:00` : time;
    }

    public getScheduledChannelSnapshots(): Observable<ScheduledChannelSnapshot[]> {
        return this.apollo
            .query({
                query: gql`
                    query SupplierSnapshotConfigs {
                        supplierSnapshotConfigs {
                            searchSupplierSnapshotConfigs {
                                totalCount
                                statusCode
                                message
                                data {
                                    id
                                    supplierName
                                    channels {
                                        channelId
                                    }
                                    nextScheduledRun
                                    lastRunStatus
                                    lastRunAt
                                    isEnabled
                                    frequencyTypeValue
                                }
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierSnapshotConfigs.searchSupplierSnapshotConfigs.data;
                }),
                map((data) => {
                    return data.map((item: any) => {
                        return {
                            id: item.id,
                            supplierName: item.supplierName,
                            channelIds: item.channels.map((channel: any) => channel.channelId),
                            schedule: {
                                frequency: item.frequencyTypeValue as ScheduledChannelSnapshotFrequency,
                            },
                            lastRunTime: item.lastRunAt,
                            lastRunStatus: {
                                status: item.lastRunStatus,
                                errors: [],
                                warnings: [],
                            },
                            nextRunTime: item.nextScheduledRun,
                            enabled: item.isEnabled,
                        };
                    });
                }),
            );
    }

    public getScheduledChannelSnapshotById(id: string): Observable<ScheduledChannelSnapshot> {
        return this.apollo
            .query({
                query: gql`
                query SupplierSnapshotConfigs {
                        supplierSnapshotConfigs {
                            searchSupplierSnapshotConfigs(
                            searchQuery: {
                                pageNumber: 1,
                                pageSize: 2000,
                                orderDirection: ASC
                            }
                            filtering: {
                                searchTerm: "${id}"
                            }
                        ) {
                            totalCount
                            statusCode
                            message
                            data {
                                id
                                supplierId
                                supplierName
                                selectedListings {
                                    listingId
                                }
                                channels {
                                    channelId
                                }
                                nextScheduledRun
                                lastRunAt
                                lastRunStatus
                                scheduleDayOfMonth
                                scheduleTime
                                isEnabled
                                frequencyTypeValue
                                scheduleDaysOfWeekTypeValue
                                listingSelectionOptionTypeValue
                                entityTracking {
                                    createdById
                                    createdBy
                                    createdAt
                                    modifiedById
                                    modifiedAt
                                    modifiedBy
                                }
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierSnapshotConfigs.searchSupplierSnapshotConfigs.data[0];
                }),
                map((item) => {
                    const mappedSelectionOptionTypeValue = this.mapInverseListingSelectionOptionTypeValue(
                        item.listingSelectionOptionTypeValue,
                    );

                    return {
                        id: item.id,
                        supplierId: item.supplierId,
                        supplierName: item.supplierName,
                        channelIds: item.channels.map((channel: any) => channel.channelId),
                        schedule: {
                            frequency: item.frequencyTypeValue as ScheduledChannelSnapshotFrequency,
                            time: item.scheduleTime,
                            daysOfWeek: this.mapDayBitmaskToObject(item.scheduleDaysOfWeekTypeValue),
                            dayOfMonth: item.scheduleDayOfMonth,
                        },
                        lastRunTime: item.lastRunAt,
                        lastRunStatus: {
                            status: item.lastRunStatus,
                            errors: [],
                            warnings: [],
                        },
                        nextRunTime: item.nextScheduledRun,
                        enabled: item.isEnabled,
                        listingSelectionOptionTypeValue: mappedSelectionOptionTypeValue,
                        selectedListings: item.selectedListings.map((listing: any) => listing.listingId),
                        entityTracking: item.entityTracking,
                    };
                }),
            );
    }

    public saveScheduledChannelSnapshot(data: Partial<ScheduledChannelSnapshot>, id: string): Observable<any> {
        if (id) {
            return this.updateScheduledChannelSnapshot(data, id);
        } else {
            return this.createScheduledChannelSnapshot(data);
        }
    }

    private updateScheduledChannelSnapshot(data: Partial<ScheduledChannelSnapshot>, id: string): Observable<any> {
        const mappedSelectionOptionTypeValue = this.mapListingSelectionOptionTypeValue(
            data.listingSelectionOptionTypeValue!,
            data.selectedListings!.length > 0,
        );
        return this.apollo
            .mutate({
                mutation: gql`
                mutation SupplierSnapshotConfigs {
                    supplierSnapshotConfigs {
                        updateSupplierSnapshotConfig(
                            updateSupplierSnapshotConfig: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                id: id,
                                supplierId: data.supplierId,
                                isEnabled: data.enabled,
                                frequencyTypeValue: Number(data.schedule!.frequency),
                                scheduleTime:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : `${this.addSecondsIfNeeded(data.schedule!.time)}`,
                                scheduleDaysOfWeekTypeValue:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : this.mapDayObjectToBitmask(data.schedule!.daysOfWeek),
                                scheduleDayOfMonth:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : data.schedule!.dayOfMonth,
                                listingSelectionOptionTypeValue: mappedSelectionOptionTypeValue,
                                selectedChannelIds: data.channelIds,
                                selectedListingIds: data.selectedListings,
                            })}
                        ) {
                            totalCount
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data;
                }),
            );
    }

    private createScheduledChannelSnapshot(data: Partial<ScheduledChannelSnapshot>): Observable<any> {
        const mappedSelectionOptionTypeValue = this.mapListingSelectionOptionTypeValue(
            data.listingSelectionOptionTypeValue!,
            data.selectedListings!.length > 0,
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation SupplierSnapshotConfigs {
                    supplierSnapshotConfigs {
                        createSupplierSnapshotConfig(
                            createSupplierSnapshotConfig:  ${this.deleteEmptyAndPrepareDataForGraphQL({
                                supplierId: data.supplierId,
                                isEnabled: data.enabled,
                                frequencyTypeValue: Number(data.schedule!.frequency),
                                scheduleTime:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : `${this.addSecondsIfNeeded(data.schedule!.time)}`,
                                scheduleDaysOfWeekTypeValue:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : this.mapDayObjectToBitmask(data.schedule!.daysOfWeek),
                                scheduleDayOfMonth:
                                    data.schedule!.frequency === ScheduledChannelSnapshotFrequency.MANUAL
                                        ? null
                                        : data.schedule!.dayOfMonth,
                                listingSelectionOptionTypeValue: mappedSelectionOptionTypeValue,
                                selectedChannelIds: data.channelIds,
                                selectedListingIds: data.selectedListings,
                            })}
                        ) {
                            totalCount
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data;
                }),
            );
    }

    private mapListingSelectionOptionTypeValue(
        selectionMode: ScheduledChannelSnapshotListingsSelectionMode,
        hasListings: boolean,
    ): ScheduledChannelSnapshotListingsSelectionMode {
        if (selectionMode === ScheduledChannelSnapshotListingsSelectionMode.INCLUDE && hasListings) {
            return ScheduledChannelSnapshotListingsSelectionMode.INCLUDE;
        } else if (selectionMode === ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE && hasListings) {
            return ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE;
        } else if (selectionMode === ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE) {
            return ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE_ALL;
        } else {
            return ScheduledChannelSnapshotListingsSelectionMode.INCLUDE_ALL;
        }
    }

    private mapInverseListingSelectionOptionTypeValue(
        selectionMode: ScheduledChannelSnapshotListingsSelectionMode,
    ): ScheduledChannelSnapshotListingsSelectionMode {
        if (selectionMode === ScheduledChannelSnapshotListingsSelectionMode.INCLUDE_ALL) {
            return ScheduledChannelSnapshotListingsSelectionMode.INCLUDE;
        } else if (selectionMode === ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE_ALL) {
            return ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE;
        }

        return selectionMode;
    }

    public deleteScheduledChannelSnapshot(id: string) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation SupplierSnapshotConfigs {
                    supplierSnapshotConfigs {
                        removeSupplierSnapshotConfig(
                            removeSupplierSnapshotConfig: { id: "${id}" }
                        ) {
                            statusCode
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data;
                }),
            );
    }

    public runScheduledSnapshot(id: string): Observable<any> {
        return this.http.post(`${SUPPLIER_MANAGEMENT_API_URL}snapshotConfig/${id}/run-now`, null);
    }
}
